/**
 * QUIPWA — in-memory notes for the Design Lab
 *
 * Same interface as LocalNotesRepository, no account and no database, so the
 * notes UI can be inspected and screenshotted (`/design-lab/notes`,
 * `/design-lab/all`). Nothing is persisted; a reload starts over.
 */
import { MODULE_ONE_FILENAME } from './course-promotion'
import {
  COURSE_INDEX_FILENAME, computeBacklinks, computeUnlinkedMentions, fieldsFromMarkdown, joinNote, noteFilename, splitNote,
  type Backlink, type LinkableNote, type NoteContent, type NoteMetadata, type UnlinkedMention,
} from './note-model'
import type { CreateNoteOptions, NotesRepository } from './notes-repository'

interface MockNote {
  id: string
  filename: string
  body: string
  parentId?: string | undefined
  folderId?: string | undefined
  modifiedAt: number
}

/** "Kitchen sink" body exercising every block type the editor supports. */
const KITCHEN_SINK = `# Synaptic Plasticity & Memory Formation

Long-term potentiation (LTP) is the persistent strengthening of synapses based on recent patterns of activity.

## Core Concepts

- [x] Define synaptic plasticity
- [ ] Explain LTP induction (NMDA receptors)
- [ ] Compare LTP vs LTD

## Math

The Hebbian learning rule is often summarized as $\\Delta w_{ij} = \\eta \\cdot x_i \\cdot x_j$.

$$
\\frac{dV}{dt} = \\frac{1}{C_m}\\left(I_{ext} - g_{Na}m^3h(V-E_{Na}) - g_K n^4 (V - E_K)\\right)
$$

## Code

\`\`\`javascript
function hebbianUpdate(preSynaptic, postSynaptic, learningRate) {
  return learningRate * preSynaptic * postSynaptic;
}
\`\`\`

## Notes

> "Cells that fire together, wire together." — Donald Hebb

Related: [[Linear Algebra — Complete Bootcamp]]

1. Ordered item one
2. Ordered item two
`

function fm(title: string, type: string, status: string, domain: string, tags: string[]): Record<string, unknown> {
  return { title, type, status, domain, tags }
}

const DAY = 86_400_000

function seed(now: number): MockNote[] {
  const note = (id: string, filename: string, meta: Record<string, unknown>, ageMs: number, content?: string, extra: Partial<MockNote> = {}): MockNote => ({
    id,
    filename,
    body: joinNote(content ?? `# ${String(meta['title'])}\n\n_(contenido de ejemplo)_\n`, meta),
    modifiedAt: now - ageMs,
    ...extra,
  })
  return [
    // 'ciencias' also exists as a deck tag in the Design Lab seed, so the shared
    // tag view shows a tag spanning decks and notes.
    note('1', 'synaptic.md', fm('Synaptic Plasticity & Memory Formation', 'Short Course', 'In Progress', 'Neuroscience', ['synapse', 'plasticity', 'LTP', 'ciencias']), 0, KITCHEN_SINK),
    note('2', 'linear.md', fm('Linear Algebra — Complete Bootcamp', 'Book', 'Pending', 'Mathematics', ['matrices', 'vectors', 'eigenvalues']), 2 * DAY),
    note('3', 'stoic.md', fm('Stoic Philosophy & Practical Ethics', 'Book', 'Mastered', 'Philosophy', ['stoicism', 'marcus-aurelius']), 5 * DAY),
    note('4', 'react.md', fm('React Internals — Fiber Architecture', 'Quick Note', 'Completed', 'Engineering', ['react', 'fiber', 'reconciler']), 3_600_000),
    // A Long Course with two modules.
    note('5', COURSE_INDEX_FILENAME, fm('Full-Stack Web Development', 'Long Course', 'In Progress', 'Engineering', ['web', 'fullstack']), DAY,
      '# Full-Stack Web Development\n\n## Módulos\n\n- [[Módulo 1 — HTML y CSS]]\n- [[Módulo 2 — JavaScript moderno]]\n', { folderId: 'mock_folder_fullstack' }),
    note('5a', 'modulo_1.md', fm('Módulo 1 — HTML y CSS', 'Short Course', 'Completed', 'Engineering', ['html', 'css']), 3 * DAY, undefined, { parentId: '5' }),
    note('5b', 'modulo_2.md', fm('Módulo 2 — JavaScript moderno', 'Short Course', 'In Progress', 'Engineering', ['javascript']), 2 * DAY, undefined, { parentId: '5' }),
  ]
}

export class MockNotesRepository implements NotesRepository {
  private readonly notes = new Map<string, MockNote>()
  private readonly listeners = new Set<() => void>()
  private counter = 0
  private readonly now: () => number

  constructor(now: () => number = Date.now) {
    this.now = now
    for (const n of seed(now())) this.notes.set(n.id, n)
  }

  private meta(n: MockNote): NoteMetadata {
    const f = fieldsFromMarkdown(n.body, n.filename)
    const parent = n.parentId ? this.notes.get(n.parentId) : undefined
    return {
      file_id: n.id,
      filename: n.filename,
      title: f.title,
      tags: f.tags,
      domain: f.domain || undefined,
      status: f.status,
      type: f.type,
      parent_id: n.parentId,
      drive_folder_id: n.folderId ?? parent?.folderId,
      modified_at: Math.floor(n.modifiedAt / 1000),
      size_bytes: n.body.length,
    }
  }

  private linkable(): LinkableNote[] {
    return [...this.notes.values()].map((n) => {
      const f = fieldsFromMarkdown(n.body, n.filename)
      return { id: n.id, title: f.title, type: f.type, body: n.body }
    })
  }

  private emit(): void {
    for (const l of this.listeners) l()
  }

  async list(): Promise<NoteMetadata[]> {
    return [...this.notes.values()].sort((a, b) => b.modifiedAt - a.modifiedAt).map((n) => this.meta(n))
  }

  async read(id: string): Promise<NoteContent | null> {
    const n = this.notes.get(id)
    if (!n) return null
    const { content, frontmatter } = splitNote(n.body)
    return { file_id: n.id, filename: n.filename, content, frontmatter }
  }

  async save(id: string, content: string, frontmatter: Record<string, unknown>): Promise<void> {
    const n = this.notes.get(id)
    if (!n) throw new Error(`Nota no encontrada: ${id}`)
    n.body = joinNote(content, frontmatter)
    n.modifiedAt = this.now()
    this.emit()
  }

  async create(content: string, frontmatter: Record<string, unknown>, options: CreateNoteOptions = {}): Promise<string> {
    const id = `mock_${(++this.counter).toString(36)}_${this.now().toString(36)}`
    const body = joinNote(content, frontmatter)
    const f = fieldsFromMarkdown(body, 'nota.md')
    const parent = options.parentId ? this.notes.get(options.parentId) : undefined
    this.notes.set(id, {
      id,
      filename: !parent && f.type === 'Long Course' ? COURSE_INDEX_FILENAME : noteFilename(f.title),
      body,
      parentId: parent?.id,
      folderId: !parent && f.type === 'Long Course' ? `mock_folder_${id}` : undefined,
      modifiedAt: this.now(),
    })
    this.emit()
    return id
  }

  async remove(id: string): Promise<string[]> {
    const n = this.notes.get(id)
    if (!n) return []
    const removed = [id, ...[...this.notes.values()].filter((m) => m.parentId === id).map((m) => m.id)]
    for (const r of removed) this.notes.delete(r)
    this.emit()
    return removed
  }

  async promoteToLongCourse(id: string): Promise<string> {
    const n = this.notes.get(id)
    if (!n) throw new Error('No se encontró la nota')
    const f = fieldsFromMarkdown(n.body, n.filename)
    if (f.type === 'Long Course') return id
    if (n.parentId) throw new Error('Un módulo no puede convertirse en curso')
    const courseId = await this.create(
      `# ${f.title}\n\n## Módulos\n\n- [[${f.title}]]\n`,
      fm(f.title, 'Long Course', f.status, f.domain, f.tags)
    )
    n.parentId = courseId
    n.filename = MODULE_ONE_FILENAME
    this.emit()
    return courseId
  }

  async backlinks(id: string): Promise<Backlink[]> {
    return computeBacklinks(this.linkable(), id)
  }

  async unlinkedMentions(title: string, excludeId: string): Promise<UnlinkedMention[]> {
    return computeUnlinkedMentions(this.linkable(), title, excludeId)
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
}
